import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  Alert,
  Paper,
  Grid,
  Divider,
} from '@mui/material';
import { Google } from '@mui/icons-material';
import PasswordField from '../components/PasswordField';
import PasswordRequirements from '../components/PasswordRequirements';
import { validatePassword, isPasswordValid, PasswordRequirements as PasswordRequirementsType } from '../utils/passwordValidation';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [passwordRequirements, setPasswordRequirements] = useState<PasswordRequirementsType>({
    minLength: false,
    hasLowercase: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Real-time password validation
  useEffect(() => {
    if (formData.password) {
      const requirements = validatePassword(formData.password);
      setPasswordRequirements(requirements);
    }
  }, [formData.password]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    const { firstName, lastName, email, password, confirmPassword } = formData;

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setLoading(false);
      return;
    }

    if (!isPasswordValid(passwordRequirements)) {
      setErrorMessage('Password does not meet requirements');
      setLoading(false);
      return;
    }

    try {
      await Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          given_name: firstName,
          family_name: lastName,
          name: `${firstName} ${lastName}`,
        },
      });
      
      setVerificationSent(true);
    } catch (error) {
      console.error('Error signing up:', error);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      await Auth.confirmSignUp(formData.email, verificationCode);
      
      // Sign in the user after successful verification
      const user = await Auth.signIn(formData.email, formData.password);
      
      // Store the access token in localStorage for axios
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();
      localStorage.setItem('aws-token', accessToken);
      
      navigate('/tests');
    } catch (error) {
      console.error('Error confirming sign up:', error);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const result = await Auth.federatedSignIn();
      // The user will be redirected to Google OAuth flow
      // Callback will be handled by Amplify automatically
    } catch (error) {
      console.error('Error with Google sign in:', error);
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred with Google sign in');
      }
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            {verificationSent ? 'Verify Your Email' : 'Create an Account'}
          </Typography>
          
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          
          {!verificationSent ? (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="firstName"
                    label="First Name"
                    name="firstName"
                    autoComplete="given-name"
                    autoFocus
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="lastName"
                    label="Last Name"
                    name="lastName"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <PasswordField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    id="password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    showPassword={showPasswords.password}
                    onTogglePassword={(show) => setShowPasswords(prev => ({ ...prev, password: show }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <PasswordField
                    margin="normal"
                    required
                    fullWidth
                    name="confirmPassword"
                    label="Confirm Password"
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    showPassword={showPasswords.confirmPassword}
                    onTogglePassword={(show) => setShowPasswords(prev => ({ ...prev, confirmPassword: show }))}
                  />
                </Grid>
              </Grid>
              
              {/* Password Requirements */}
              {formData.password && (
                <PasswordRequirements requirements={passwordRequirements} />
              )}
              
              {/* Password Mismatch Warning */}
              {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Passwords do not match
                </Alert>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.confirmPassword || !isPasswordValid(passwordRequirements) || formData.password !== formData.confirmPassword}
              >
                {loading ? 'Signing up...' : 'Sign Up'}
              </Button>
              
              <Divider sx={{ my: 2 }}>or</Divider>
              
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Google />}
                onClick={handleGoogleSignIn}
                disabled={loading}
                sx={{ mb: 2 }}
              >
                Continue with Google
              </Button>
              
              <Box sx={{ textAlign: 'center' }}>
                <Link component={RouterLink} to="/login" variant="body2">
                  {"Already have an account? Sign In"}
                </Link>
              </Box>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleVerification} noValidate>
              <Typography variant="body1" sx={{ mb: 2 }}>
                We've sent a verification code to your email address. Please enter it below to verify your account.
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                id="verificationCode"
                label="Verification Code"
                name="verificationCode"
                autoFocus
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Verify'}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default RegisterPage;
